import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    alpha,
    useTheme
} from '@mui/material';
import {
    Event as EventIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    Group as GroupIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

export default function PartyCard({ party, onEdit, onDelete, onInvite }) {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        handleMenuClose();
        action(party);
    };

    const formatPartyDate = (dateValue) => {
        try {
            if (!dateValue) return 'Date TBD';
            const date = dateValue?.toDate ? dateValue.toDate() : dateValue;
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid Date';
            return format(dateObj, 'MMM dd, yyyy');
        } catch (error) {
            return 'Date Error';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'upcoming': return theme.palette.primary.main;
            case 'ongoing': return theme.palette.success.main;
            case 'completed': return theme.palette.text.secondary;
            default: return theme.palette.text.secondary;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'upcoming': return 'Upcoming';
            case 'ongoing': return 'Ongoing';
            case 'completed': return 'Completed';
            default: return 'Unknown';
        }
    };

    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: getStatusColor(party.status),
                        borderRadius: '4px 0 0 4px'
                    }
                }}
            >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
                                {party.name}
                            </Typography>
                            <Chip
                                label={getStatusLabel(party.status)}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backgroundColor: alpha(getStatusColor(party.status), 0.1),
                                    color: getStatusColor(party.status),
                                    border: `1px solid ${alpha(getStatusColor(party.status), 0.2)}`
                                }}
                            />
                        </Box>
                        <IconButton size="small" onClick={handleMenuOpen} sx={{ mt: -1, mr: -1 }}>
                            <MoreVertIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    p: 0.8,
                                    borderRadius: '8px',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    display: 'flex'
                                }}
                            >
                                <EventIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                {formatPartyDate(party.date)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    p: 0.8,
                                    borderRadius: '8px',
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                    color: 'secondary.main',
                                    display: 'flex'
                                }}
                            >
                                <LocationIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {party.location || 'Location TBD'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    p: 0.8,
                                    borderRadius: '8px',
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    color: 'info.main',
                                    display: 'flex'
                                }}
                            >
                                <GroupIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {party.groupName || 'Unknown Group'}
                            </Typography>
                        </Box>
                    </Box>

                    {party.notes && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                border: `1px solid ${alpha('#fff', 0.05)}`
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                "{party.notes.length > 80 ? `${party.notes.substring(0, 80)}...` : party.notes}"
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        minWidth: 160,
                        mt: 1
                    }
                }}
            >
                <MenuItem onClick={() => handleAction(onEdit)}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit Details</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleAction(onInvite)}>
                    <ListItemIcon>
                        <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Invite Guests</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleAction(onDelete)} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Delete Party</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
